import React, { useEffect, useMemo, useState, useCallback } from "react";

const JSON_URL = "/graphs/index.json"; // Netlify/localhost palvelee tämän public/graphs-kansiosta

export default function App() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [chain, setChain] = useState("all");

  // --- Modal state for "Important info"
  const [showInfo, setShowInfo] = useState(false);
  const openInfo = useCallback(() => setShowInfo(true), []);
  const closeInfo = useCallback(() => setShowInfo(false), []);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeInfo();
    };
    if (showInfo) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showInfo, closeInfo]);

  const fetchIndex = async () => {
    try {
      setError("");
      setLoading(true);
      const res = await fetch(JSON_URL, { cache: "no-store" });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("No graphs found.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIndex();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((g) => {
      const matchesChain =
        chain === "all" || (g.chain || "").toLowerCase() === chain;
      const hay = [g.title, g.filename, g.token, g.created_at]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesQuery = term === "" || hay.includes(term);
      return matchesChain && matchesQuery;
    });
  }, [items, q, chain]);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso || "";
    }
  };

  const getThumb = (g) => {
    const name =
      g.thumbnail || (g.filename ? g.filename.replace(/\.html?$/i, ".png") : "");
    return name ? `/graphs/${name}` : "";
  };

  return (
    <div className="app">
      <header className="topbar" style={{ display: "flex", alignItems: "center" }}>
        <div className="brand-row">
          <div className="brand">TokenDNA • ETH</div>
          <button className="link" onClick={openInfo} aria-haspopup="dialog" aria-controls="info-modal">
            Important info
          </button>
        </div>

        <div className="actions">
          <button className="btn" onClick={fetchIndex} disabled={loading}>
            {loading ? "Updating..." : "Update list"}
          </button>
        </div>
      </header>

      {/* Info modal */}
      {showInfo && (
        <div
          className="modal-backdrop"
          onClick={closeInfo}
          aria-hidden="true"
        >
          <div
            className="modal"
            id="info-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="info-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="info-title">Platform Overview</h2>
              <button className="modal-close" onClick={closeInfo} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <p>
                TokenDNA provides professional blockchain forensics and scam-token detection.
                Our system continuously monitors newly deployed contracts on Ethereum,
                highlighting suspicious launches based on transaction data, just after launches.
              </p>
              <p>
                Each graph represents a full transaction network: funding sources to two levels, buyers,
                liquidity pools, and potential connections between wallets. This enables
                deeper investigation into hidden patterns of market manipulation and fraud.
              </p>
              <p>
                Use the search and filtering tools to explore the database of analyzed
                tokens. The goal is simple: deliver transparent insights that help protect
                communities and researchers from financial scams.
              </p>
              <p>
                <strong>Note:</strong> This is a tool, which uses Ethereum blockchain data. It does not provide any kind of
                investment advice or guarantee accuracy. It shows only max first 50 buyers activity.
                Upcoming premium tg bot gives possibility to deep dive all buyers of token.
              </p>
              <p className="warning-text">
                <strong>Always conduct your own research before engaging with any token!!</strong>
              </p>

              <h2>How to use the graphs</h2>
              <ul>
                <li>
                  Each graph visualizes the transaction network of a specific token,
                  showing how funds flow from initial sources to buyers and liquidity pools.
                  Click the thumbnail to open the interactive version in a new tab.
                  Nodes are clickable (Etherscan). Hover shows details (age, transferred ETH, etc.).
                </li>
                <li>
                  Nodes represent wallets/contracts; edges are transactions.
                  <ul>
                    <li>Circles = First buyers. Color = wallet age (red new ≤7d, yellow 7–30d, green old)</li>
                    <li>Squares = Normal funders (blue)</li>
                    <li>Diamonds = Multisenders (exchanges, LPs, disperse)</li>
                    <li>Golden cross = level 2 funder wallets</li>
                  </ul>
                </li>
                <li>
                  Edges show direction and relative size (thickness).
                  <ul>
                    <li>Red = Bulk send (same tx → many wallets)</li>
                    <li>Blue = Normal send funder → buyer</li>
                    <li>Magenta = Send from multifunder → buyer</li>
                  </ul>
                </li>
                <li>
                  Look for: centralized hubs, rapid LP moves, clusters linked to known scam wallets.
                </li>
                <li>
                  Use search to find tokens by name, contract, or keywords.
                </li>
              </ul>
              <p>
                By analyzing these graphs, you can learn typical red flags and patterns behind manipulative practices.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={closeInfo}>Close</button>
            </div>
          </div>
        </div>
      )}

      <main className="container">
        {/* Korvataan aiempi info-osio pienellä vihjeellä */}
        <section className="info compact-info">
          <span>Read the platform overview and usage tips →</span>
          <button className="btn primary" onClick={openInfo}>Important info</button>
        </section>

        <section className="filters">
          <input
            className="input"
            placeholder="Search: contract, token name"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select
            className="select"
            value={chain}
            onChange={(e) => setChain(e.target.value)}
          >
            {/* <option value="all">All chains</option> */}
            <option value="ethereum">Ethereum</option>
            {/* <option value="bsc">BSC</option>
            <option value="solana">Solana</option> */}
          </select>
        </section>

        {error && <div className="alert error">{error}</div>}

        {loading ? (
          <div className="empty">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="empty">
            No graphs found.
            <div className="hint">
              Try adjusting your search or filter to find what you're looking for.
            </div>
          </div>
        ) : (
          <ul className="card-grid">
            {filtered.map((g) => {
              const href = `/graphs/${g.filename}`;
              const thumb = getThumb(g);
              const title = g.title || g.filename;
              return (
                <li className="card2" key={g.filename}>
                  <a
                    className="card2-link"
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Open graph ${title}`}
                  >
                    <div className="thumb-wrap">
                      {thumb ? (
                        <img
                          className="thumb-img"
                          src={thumb}
                          alt={title}
                          loading="lazy"
                        />
                      ) : (
                        <div className="thumb-fallback">No preview</div>
                      )}
                      <div className="thumb-overlay">
                        <span className="chip">
                          {(g.chain || "").toUpperCase() || "CHAIN"}
                        </span>
                      </div>
                    </div>
                  </a>
                  <div className="caption">
                    <div className="caption-title" title={title}>
                      {title}
                    </div>
                    <div className="caption-meta">
                      {g.token && <span className="mono">{g.token}</span>}
                      <span className="spacer" />
                      <span className="time">{fmt(g.created_at)}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>

      <footer className="foot">© {new Date().getFullYear()} TokenDNA</footer>
    </div>
  );
}
